import {
    CalendarExternalGetResponse,
    ConnectedAccountGet,
    ExternalAccountGet,
    Ulid,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, computed } from "@preact/signals";
import { store } from "~/core/store";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import {
    ConnectedAccount,
    ConnectionInstance,
    ExternalAccount,
    ExternalCalendar,
    ExternalService,
} from "./interfaces";

export const makeAccount = ({ connection }: ApiSliceParams) => {
    const calendarEmitter = createEmitter<ExternalCalendar>();
    const connectedList = createListController<
        ConnectedAccountGet,
        ConnectedAccount
    >({
        connection,
        list: (con) => con.connectedAccount.items(),
    });
    const externalList = createListController<
        ExternalAccountGet,
        ExternalAccount
    >({
        connection,
        list: (con) => con.externalAccount.items(),
    });
    const calendarList = createListController<
        CalendarExternalGetResponse,
        ExternalCalendar
    >({
        connection,
        list: (con) => con.time.calendar.external.items(),
        emit: calendarEmitter.emit,
    });
    const connectedStore: Record<
        string,
        ReadonlySignal<ConnectionInstance>
    > = {};
    let activeStore: ReadonlySignal<ConnectionInstance> | null = null;

    const listen = () => {
        connectedList.listen();
        externalList.listen();
        calendarList.listen();

        const updateSelected = () => {
            const outlookCalendars = calendarList.items
                .peek()
                .filter((i) => i.service === "outlook" && i.active);
            const googleCalendars = calendarList.items
                .peek()
                .filter((i) => i.service === "google_cal" && i.active);

            store.settings.syncCalendars(
                "outlook",
                outlookCalendars.map((c) => ({
                    id: c.calendar_id,
                    isDefault: false,
                })),
            );
            store.settings.syncCalendars(
                "google_cal",
                googleCalendars.map((c) => ({
                    id: c.calendar_id,
                    isDefault: false,
                })),
            );
        };

        calendarList.whenLoaded().then(() => {
            updateSelected();
            calendarEmitter.on("add", updateSelected);
            calendarEmitter.on("remove", updateSelected);
        });
    };

    const connect = async (service: ExternalService) => {
        const con = connection.value;
        const { url } = await con.connectedAccount.connect({ service });

        const wnd = window.open("about:blank", service, "width=600,height=600");
        const form = document.createElement("form");

        if (!wnd) {
            // @TODO: try in new tab
            throw new Error("Could not open window");
        }

        form.method = "POST";
        form.action = url;
        form.target = service;
        form.style.display = "none";

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const disconnect = async (service: ExternalService) => {
        const con = connection.value;

        // disconnect all for now, until we have multiple accounts management
        //
        const accounts = connectedList.items
            .peek()
            .filter((i) => i.service === service);

        for (const account of accounts) {
            await con.connectedAccount.disconnect(account._id);
        }
    };

    const toggleCalendar = async (id: Readonly<Ulid>, active: boolean) => {
        const con = connection.value;

        if (active) {
            await con.time.calendar.external.activate({ id });
        } else {
            await con.time.calendar.external.deactivate({ id });
        }
    };

    const instance = (service: ExternalService) => {
        if (!(service in connectedStore)) {
            connectedStore[service] = computed(() => {
                const connected = connectedList.items.value.find(
                    (item) => item.service === service,
                );
                const external = externalList.items.value.find(
                    (item) => item.id === connected?.account.toString(),
                );
                const calendars = calendarList.items.value.filter(
                    (item) => item.service === service,
                );
                const loading =
                    connectedList.allLoading.value ||
                    externalList.allLoading.value ||
                    calendarList.allLoading.value;
                const loaded =
                    connectedList.allLoaded.value &&
                    externalList.allLoaded.value &&
                    calendarList.allLoaded.value;

                if (!connected || !external) {
                    return {
                        service,
                        active: false,
                        connected: null,
                        external: null,
                        calendars,
                        loading,
                        loaded,
                    };
                }

                const rd = new Date(Number(external.refresh_exp));
                const active = rd.getTime() > Date.now();

                return {
                    service,
                    active,
                    connected,
                    external,
                    calendars,
                    loading,
                    loaded,
                };
            });
        }

        return connectedStore[service];
    };

    const active = () => {
        if (!activeStore) {
            activeStore = computed(() => {
                const google = instance("google_cal").value;
                const outlook = instance("outlook").value;

                if (google.active) {
                    return google;
                }

                return outlook;
            });
        }

        return activeStore;
    };

    return {
        calendarList,
        onCalendar: calendarEmitter.on,
        offCalendar: calendarEmitter.off,
        instance,
        active,
        listen,
        connect,
        disconnect,
        toggleCalendar,
    };
};
